import { useGetProjectScansWithOutputsOnlyQuery } from "api/project/queries/useGetProjectScansWithOutputsOnlyQuery";
import useViewerWrapper from "hooks/useViewerWrapper";
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ScanDetails } from "types/scan";

type ScanWrapper = {
  scan: ScanDetails | undefined;
  dateText: string;
  ptcldOutputId: string;
  vtourOutputId: string;
  scanId: string;
  handleNextScan: () => void;
  handlePrevScan: () => void;
  hasNext: boolean;
  hasPrev: boolean;
};

type LoadingContextType = {
  scans1Loading: boolean;
  scans1Error: Error | null;
  scans2Loading: boolean;
  scans2Error: Error | null;
  loading: boolean;
  progress: number;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  blurryEffect: boolean;
  setBlurryEffect: React.Dispatch<React.SetStateAction<boolean>>;
  scans1: OutputsOnlyQueryType | undefined;
  scans2: OutputsOnlyQueryType | undefined;
  setPage1: React.Dispatch<
    React.SetStateAction<{
      page: number | undefined;
      earlierPage: number | undefined;
    }>
  >;
  setPage2: React.Dispatch<
    React.SetStateAction<{
      page: number | undefined;
      earlierPage: number | undefined;
    }>
  >;
  vtourOutputId1: string;
  vtourOutputId2?: string;
  ptcldOutputId1: string;
  ptcldOutputId2?: string;
  gsplatOutputId1: string;
  gsplatOutputId2?: string;
  scan1Data: ScanDetails | undefined;
  scan2Data?: ScanDetails;
  scan1: string;
  scan2?: string;
  scan1Wrapper: ScanWrapper;
  scan2Wrapper: ScanWrapper;
  viewerHeight: string;
  setViewerHeight: React.Dispatch<React.SetStateAction<string>>;
  fullscreen: boolean;
  setFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  parentRef: React.RefObject<HTMLDivElement>;
};

interface SearchParams {
  type: "vtour" | "ptcld";
  mode: "uniscan" | "uniscan-compare" | "biscan";
}

const LoadingContext = createContext<LoadingContextType | null>(null);

export const LoadingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // parent of the viewer
  const parentRef = useRef<HTMLDivElement>(null);

  const [uiLoading, setUiLoading] = useState(true);
  const [uiProgress, setUiProgress] = useState(0);

  const [blurryEffect, setBlurryEffect] = useState(false);
  const [viewerHeight, setViewerHeight] = useState("100vh");

  const [fullscreen, setFullscreen] = useState(false);
  
  const [page1, setPage1] = useState<{
    page: number | undefined;
    earlierPage: number | undefined;
  }>({
    page: undefined,
    earlierPage: undefined,
  });

  const [page2, setPage2] = useState<{
    page: number | undefined;
    earlierPage: number | undefined;
  }>({
    page: undefined,
    earlierPage: undefined,
  });

  const { projectSlug = "", workspaceSlug = "", scanId = "" } = useParams();

  const [queryParams, setQueryParams] = useSearchParams();

  const mode = (queryParams.get("mode") as SearchParams["mode"]) || "uniscan";

  const scan1Id =
    mode === "uniscan" ? scanId : queryParams.get("scan1") || scanId;
  const scan2Id = mode === "uniscan" ? "" : queryParams.get("scan2") || scanId;

  const {
    data: scans1,
    isLoading: scans1Loading,
    error: scans1Error,
  } = useGetProjectScansWithOutputsOnlyQuery(
    projectSlug,
    scan1Id,
    workspaceSlug,
    page1.page,
    page1.earlierPage,
    "scene1"
  );

  const {
    data: scans2,
    isLoading: scans2Loading,
    error: scans2Error,
  } = useGetProjectScansWithOutputsOnlyQuery(
    projectSlug,
    scan2Id,
    workspaceSlug,
    page2.page,
    page2.earlierPage,
    "scene2"
  );

  // console.log("scans1", scans1);
  // console.log("scans2", scans2);

  const scan1Wrapper = useViewerWrapper(scans1, setPage1);

  const scan2Wrapper = useViewerWrapper(scans2, setPage2);

  const value = {
    scans1Loading,
    scans1Error,
    scans2Loading,
    scans2Error,
    loading: uiLoading,
    progress: uiProgress,
    setLoading: setUiLoading,
    setProgress: setUiProgress,
    blurryEffect,
    setBlurryEffect,
    scans1,
    scans2,
    setPage1,
    setPage2,
    vtourOutputId1: scan1Wrapper?.vtourOutputId,
    vtourOutputId2: scan2Wrapper?.vtourOutputId,
    ptcldOutputId1: scan1Wrapper?.ptcldOutputId,
    ptcldOutputId2: scan2Wrapper?.ptcldOutputId,
    gsplatOutputId1: scan1Wrapper?.gsplatOutputId,
    gsplatOutputId2: scan2Wrapper?.gsplatOutputId,
    scan1Data: scan1Wrapper?.scan,
    scan2Data: scan2Wrapper?.scan,
    scan1: scan1Wrapper?.scanId,
    scan2: scan2Wrapper?.scanId,
    scan1Wrapper,
    scan2Wrapper,
    viewerHeight,
    setViewerHeight,
    fullscreen,
    setFullscreen,
    parentRef,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};
